/* eslint-disable max-lines */
import {
  ffNativeWs,
  hasFeatureFlag,
  logger,
  ObjUtil,
  Time,
  TimeDiff,
  Util,
  UUID
} from 'yougile-util'
import {
  DataObjectId,
  GenReq,
  GenResp,
  InitResp,
  minorDataTransmissionVersion,
  ReportReq,
  TimeResp
} from 'yougile-model'
import {ConnectorReadiness} from '@client/connector/connector-readiness'
import {globals} from '@extensions/globals-base'
import {Cookies} from '../app/client/cookies'
import {DesktopRenderer} from '../app/client/desktop-renderer'
import {Storage} from '../app/client/storage'
import {BackendReportLogger} from './backend-report-logger'

const REPORT_VERSION = '1.0'

export class Backend {
  private static endpoint = ''
  private static defaultToNativeWs = false
  private static endpointInitializedTime = 0
  private static userId = '' as DataObjectId
  private static key = ''
  private static backendDataUrlWarnShown = false
  static uploadFileLimit = 50
  static features: NonNullable<InitResp['features']> = {}
  static internationalPhoneNumbers = false

  // For local front with remote backend
  static shouldUseLocalhostForAllRequests = (): boolean =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    import.meta.env.VITE_USE_LOCALHOST === 'true'

  /**
   * Sometimes it takes long to load data from Storage.
   * key -> value
   */
  private static localCache: Map<string, string> = new Map()

  static latestRequestTime = 0
  static latestResponseTime = 0

  private static localListeners: Map<string, Map<UUID, () => void>> = new Map()
  private static host = ''
  private static dataUrl = ''
  public static wsUrl = ''
  static appVersion = ''
  static ip = ''
  static initResponse: InitResp | undefined = undefined
  private static requireLogout = false
  private static keyExpirationTimeout = 300 * 24 * 3600_000
  private static scalableAccountCluster = false
  static analyticPlatformUrl: string | undefined = undefined
  static analyticPlatformToken: string | undefined = undefined

  static shouldUseNativeWs(): boolean {
    const preferNative = hasFeatureFlag(ffNativeWs) || this.defaultToNativeWs
    return (Backend.features.nativeWs && preferNative) ?? false
  }

  static setCredentials(userId: DataObjectId, key: string): void {
    Backend.userId = userId
    Backend.key = key
  }

  static setHost(host: string): void {
    if (host.startsWith('http') && host.includes('//')) {
      Backend.host = host
    } else {
      if (host.startsWith('//')) {
        Backend.host = 'https:' + host
      } else {
        Backend.host = 'https://' + host
      }
    }
  }

  static getHost(): string {
    return this.host
  }

  static usesProxy(): boolean {
    return location.host.includes('-proxy')
  }

  static processInitResp(res: InitResp): void {
    Backend.initResponse = res
    globals.standalone = Boolean(res.standalone)
    if (res.dataUrl?.startsWith('//')) {
      res.dataUrl = 'https:' + res.dataUrl
    }
    if (Backend.shouldUseLocalhostForAllRequests()) {
      res.dataUrl = window.location.protocol + '//' + window.location.host
    }
    Backend.dataUrl = res.dataUrl || Backend.host
    Backend.wsUrl = res.wsUrl
    if (this.usesProxy()) {
      Backend.dataUrl = Backend.host
      Backend.wsUrl = ''
    }
    Backend.appVersion = res.version ?? ''
    Backend.ip = res.ip ?? ''
    Backend.requireLogout = res.requireLogout ?? false
    const resFeatutes = res.features
    if (resFeatutes) {
      const {features} = Backend
      Object.keys(resFeatutes).forEach((k: keyof typeof features) => {
        features[k] = resFeatutes[k]
      })
    }
    if (res.uploadFileLimit) {
      Backend.uploadFileLimit = res.uploadFileLimit
    }
    if (res.keyExpirationTimeout) {
      this.keyExpirationTimeout = res.keyExpirationTimeout
    }
    if (res.internationalPhoneNumbers) {
      this.internationalPhoneNumbers = res.internationalPhoneNumbers
    }
    if (res.scalableAccountCluster) {
      this.scalableAccountCluster = res.scalableAccountCluster
      this.endpoint = Backend.host ?? Backend.dataUrl
      this.defaultToNativeWs = true
    }
    this.analyticPlatformUrl = res.analyticPlatformUrl
    this.analyticPlatformToken = res.analyticPlatformToken
  }

  private static async initDataHost(): Promise<InitResp> {
    try {
      const res = (await Backend.requestRawJSON({
        url: '/data/init',
        method: 'GET',
        data: undefined,
        useCurrentHost: true,
        attemptsMade: 4
      })) as InitResp
      if (res) {
        const saved = JSON.stringify(res)
        Backend.saveLocal('data-init', saved)
      }
      Backend.processInitResp(res)
      return res
    } catch (e) {
      // throw new Error('Can’t reach the server');
      logger.error('Can’t reach the server')
      const resStr = Backend.loadLocal('data-init')
      if (resStr) {
        const res = JSON.parse(resStr)
        if (res) {
          Backend.processInitResp(res)
          return res
        }
      }
      if (Util.isDesktop()) {
        const res: InitResp = {
          dataUrl: '',
          wsUrl: '',
          ip: '',
          uploadFileLimit: 50,
          result: 'ok'
        }
        Backend.processInitResp(res)
        return res
      }
      throw new Error('Can’t reach the server')
    }
  }

  static toDataUrl(path: string): string
  static toDataUrl(path: string | undefined): string | undefined {
    if (!path) {
      return path
    }
    if (path.includes('//')) {
      if (path.startsWith('//')) {
        return 'https:' + path
      } else {
        return path
      }
    } else {
      return Backend.dataHost() + path
    }
  }

  static toMainUrl(path: string): string {
    if (path.includes('//')) {
      if (path.startsWith('//')) {
        return 'https:' + path
      } else {
        return path
      }
    } else {
      return Backend.host + path
    }
  }

  static report(...messages: Array<string | number | undefined>): void {
    const message = messages.join(' ')
    logger.warn(message)
    const req: ReportReq = {
      reqId: Util.UUID(),
      message,
      version: REPORT_VERSION,
      userId: Backend.userId
    }
    Backend.postJSON<GenResp, ReportReq>('/data/report', req).catch(e => {
      logger.warn(e)
    })
  }

  static init(): Promise<InitResp> {
    const prop = 'provide-auth-data'
    const authData = Storage.getItem(prop)
    if (authData) {
      Backend.userId = authData.split('|').shift() as DataObjectId
      Backend.key = authData.split('|').pop() || ''
      Storage.removeItem(prop)
    }
    if (Util.isDesktop()) {
      this.endpoint = ''
      const savedUrl = Backend.loadLocal('server_url')
      Backend.setHost(savedUrl || 'https://yougile.com')
    } else {
      logger.initializeWithCustom(new BackendReportLogger())
    }
    return Backend.initDataHost()
  }

  static dataHost(): string {
    if (!Backend.dataUrl && !Backend.backendDataUrlWarnShown) {
      logger.warn('Backend.dataUrl is not defined!')
      Backend.backendDataUrlWarnShown = true
    }
    return Backend.dataUrl || Backend.host
  }

  static loadScript(url: string, cb: () => void): void {
    const s = document.createElement('script')
    s.src = url
    s.charset = 'UTF-8'
    s.async = true
    document.body.appendChild(s)
    if (cb) {
      s.onload = cb
    }
  }

  private static currentEndpointPromise: Promise<string> | undefined

  static async getEndpoint(): Promise<string> {
    if (this.currentEndpointPromise !== undefined) {
      return this.currentEndpointPromise
    }
    const promise = Promise.race([
      this.makeGetEndpointRequest(),
      this.waitAndFallbackToCurrentEndpoint()
    ])
    this.currentEndpointPromise = promise
    try {
      const res = await promise
      return res
    } finally {
      this.currentEndpointPromise = undefined
    }
  }

  private static async waitAndFallbackToCurrentEndpoint(): Promise<string> {
    await Util.wait(20_000)
    return this.endpoint || this.host
  }

  private static async makeGetEndpointRequest(): Promise<string> {
    if (this.scalableAccountCluster && this.endpoint) {
      return this.endpoint
    }
    const timeout = ConnectorReadiness.ready ? 3 * Time.minute : 30_000
    const now = Date.now()
    if (
      this.endpointInitializedTime &&
      now - this.endpointInitializedTime < timeout
    ) {
      return this.endpoint || this.host
    }

    this.endpointInitializedTime = Date.now()
    this.loadId()
    try {
      const time = await this.getJSON<TimeResp>(
        '/data/time-v2?t=' + Date.now(),
        {useCurrentHost: true}
      )
      if (time?.result === 'ok' && !!time.version) {
        const acc = await this.postJSON<{
          url: string
          defaultToNativeWs: boolean | undefined
        }>(
          '/data/endpoint',
          {userId: this.userId ?? 'empty'},
          {useCurrentHost: true}
        )
        if (acc.result === 'ok') {
          if (acc.url && !Backend.shouldUseLocalhostForAllRequests()) {
            this.endpoint = acc.url
          }
          this.defaultToNativeWs = Boolean(acc.defaultToNativeWs)
        }
      }
    } catch (e) {
      logger.error('Error in ensureEndpoint', e)
    }
    return this.endpoint || this.host
  }

  static async requestRawJSON(opts: {
    url: string
    method: 'GET' | 'POST'
    data?: unknown
    attemptsMade?: number
    useCurrentHost?: boolean
  }): Promise<unknown> {
    let {data} = opts
    const {url, method, attemptsMade = 0, useCurrentHost} = opts
    Backend.latestRequestTime = Time.now()
    const request = new XMLHttpRequest()

    const fullUrl = await this.getFullUrl({url, useCurrentHost})

    request.open(method, fullUrl, true)
    request.setRequestHeader('Content-Type', 'application/json; charset=UTF-8')

    if (data) {
      const r = JSON.stringify(data)
      if (r.length > 3000000) {
        Backend.report('Large POST skipped', r.length)
        return Promise.resolve(undefined)
      }
    }

    const retry = (
      err: Error,
      resolve: (r: unknown) => void,
      reject: (e: Error) => void
    ) => {
      if (attemptsMade > 5) {
        reject(err)
      } else {
        setTimeout(
          () => {
            Backend.requestRawJSON({...opts, attemptsMade: attemptsMade + 1})
              .then(res => resolve(res))
              .catch(er => reject(er))
          },
          (attemptsMade + 1) * 500
        )
      }
    }

    return new Promise<unknown>((resolve, reject) => {
      request.onload = () => {
        if (request.status >= 200 && request.status < 400) {
          Backend.latestResponseTime = Time.now()
          const json = JSON.parse(request.responseText)
          resolve(json)
        } else {
          const {status = '?', statusText = '—'} = request
          const err = new NetworkError(
            `${url} Response status: ${status}, text: ${statusText}`,
            {}
          )
          err.status = request.status
          logger.warn(err)
          if (status === 429) {
            reject(err)
          } else {
            retry(err, resolve, reject)
          }
        }
      }
      request.onerror = evt => {
        logger.warn('Network error', evt)
        retry(new Error('Network error'), resolve, reject)
      }
      data = data || {}
      ;(data as GenReq).v = minorDataTransmissionVersion
      ;(data as GenReq).appVersion = Backend.appVersion
      ;(data as GenReq).clientType = Util.getClientType()

      if (data && method === 'POST') {
        request.send(JSON.stringify(data))
      } else {
        request.send()
      }
    })
  }

  private static async getFullUrl(opts: {
    url: string
    useCurrentHost?: boolean
  }): Promise<string> {
    const {url, useCurrentHost} = opts

    const isFullUrl =
      url.startsWith('http') &&
      (url.startsWith('https://dist.yougile.com/') ||
        url.startsWith('https://data.yougile.com/') ||
        url.startsWith(Backend.dataHost()) ||
        url.startsWith(Backend.host))

    if (isFullUrl) {
      return url
    }

    if (useCurrentHost) {
      return this.host + url
    }
    const endpoint = await this.getEndpoint()
    return endpoint + url
  }

  static getJSON<T>(
    url: string,
    opts?: {
      data?: unknown
      useCurrentHost?: boolean
    }
  ): Promise<T & GenResp> {
    const {data, useCurrentHost} = opts ?? {}
    const postfix = data
      ? (url.includes('?') ? '&' : '?') +
        ObjUtil.list(
          data,
          (k: string, v: string) =>
            `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
        ).join('&')
      : ''
    return Backend.requestRawJSON({
      url: url + postfix,
      method: 'GET',
      useCurrentHost
    }).then((resp: T & GenResp) => {
      if (resp.result === 'ok') {
        return resp
      } else {
        if (resp.error) {
          throw resp.error
        } else {
          throw new Error('result is not ok')
        }
      }
    })
  }

  static ongoingPostJSONs: Map<string, Promise<unknown>> = new Map()

  static postJSON<T, U = unknown>(
    url: string,
    data: U,
    opts: {
      rawErrors?: boolean
      useCurrentHost?: boolean
      reuseRequests?: boolean
      /** Time in ms until which data is considered fresh */
      reuseMaxAge?: number
      additionalKeyData?: Record<string, unknown>
    } = {}
  ): Promise<T & GenResp> {
    const {
      rawErrors = false,
      useCurrentHost = false,
      reuseRequests = false,
      reuseMaxAge = 0,
      additionalKeyData = {}
    } = opts

    const key = JSON.stringify({
      url,
      data,
      opts,
      additionalKeyData
    })

    if (reuseRequests) {
      if (Backend.ongoingPostJSONs.has(key)) {
        return Backend.ongoingPostJSONs.get(key) as Promise<T & GenResp>
      }
    }

    const requestPromise = Backend.requestRawJSON({
      url,
      method: 'POST',
      data,
      useCurrentHost
    })
      .then((resp: T & GenResp) => {
        if (rawErrors) {
          return resp
        }
        if (resp.result === 'ok') {
          return resp
        } else {
          if (reuseRequests) {
            Backend.ongoingPostJSONs.delete(key)
          }
          const error = new Error(resp.error) as Error & {code?: number}
          if (resp.code) {
            error.code = resp.code
          }
          throw error
        }
      })
      .finally(() => {
        if (reuseRequests) {
          setTimeout(() => {
            Backend.ongoingPostJSONs.delete(key)
          }, reuseMaxAge)
        }
      })

    if (reuseRequests) {
      Backend.ongoingPostJSONs.set(key, requestPromise)
    }

    return requestPromise
  }

  static postMultipart({
    url,
    data,
    callback,
    onProgress
  }: {
    url: string
    data: FormData
    callback: (json: unknown) => void
    onProgress?: (percent: number) => void
  }): void {
    Backend.latestRequestTime = Time.now()
    const request = new XMLHttpRequest()
    request.open('POST', Backend.host + url, true)
    request.setRequestHeader('y-id', Backend.loadId())
    request.setRequestHeader('y-key', Backend.loadKey())
    request.setRequestHeader('y-cid', globals.getCompanyId())

    request.onload = () => {
      if (request.status >= 200 && request.status < 400) {
        Backend.latestResponseTime = Time.now()
        const json = JSON.parse(request.responseText)
        callback(json)
      } else {
        callback(undefined)
      }
    }
    if (onProgress) {
      request.upload.addEventListener('progress', (event: ProgressEvent) => {
        Backend.latestResponseTime = Time.now()
        const percent: number = parseInt(
          (100 * event.loaded) / event.total + '',
          10
        )
        onProgress(percent)
      })
    }
    request.onerror = () => {
      callback(undefined)
    }
    request.send(data)
  }

  static async serverTime(): Promise<TimeResp> {
    // upd для отличия запроса getEndpoint
    const url = `/data/time-v2?t=${Date.now()}&upd=1`
    return Backend.getJSON<TimeResp>(url)
  }

  static async getServerTimezone(): Promise<number> {
    const resp = await this.serverTime()

    const clientTimezoneOffset = new Date().getTimezoneOffset() * 60_000
    const gmt0StartOfDay = Time.startOfDay(Date.now()) - clientTimezoneOffset

    return Math.round((gmt0StartOfDay - resp.startOfDay) / 3_600_000)
  }

  static async initTimeUpdates(): Promise<void> {
    const getDiff = async (): Promise<TimeDiff | undefined> => {
      if (Util.isServer()) {
        return undefined
      }
      try {
        const t1 = Date.now()
        const timeResp = await Backend.serverTime()
        const t2 = Date.now()
        const roundTripTime = t2 - t1
        if (!timeResp || roundTripTime > 10000) {
          logger.warn('update time-diff failed', {
            t1,
            t2,
            timeResp,
            roundTripTime
          })
          return undefined
        }
        const clientTimeEstimation = t1 + Math.floor(roundTripTime / 2)
        const diff = timeResp.time
          ? clientTimeEstimation - timeResp.time
          : Time.getTimeDiff()
        logger.log(
          `update time-diff, t1=${t1} t2=${t2} server=${timeResp.time} rtt=${roundTripTime} diff=${diff}`
        )
        return {
          diff,
          timeZoneDiff:
            timeResp.startOfDay &&
            timeResp.date &&
            Time.fromDateStr(timeResp.date)
              ? timeResp.startOfDay - (Time.fromDateStr(timeResp.date) ?? 0)
              : Time.getTimeZoneDiff()
        }
      } catch (e) {
        logger.error('Error in getDiff', e)
        return undefined
      }
    }

    await Time.initUpdates(getDiff)
  }

  static saveLocal(key: string, value: string): void {
    Storage.setItem(key, value)
    Backend.localCache.set(key, value)
    Backend.notifyLocalKeyChanged(key)
  }

  static loadLocal(key: string): string | undefined {
    const val = Backend.localCache.get(key)
    if (val) {
      return val
    } else {
      const res = Storage.getItem(key)
      if (res === undefined) {
        Backend.localCache.delete(key)
      } else {
        Backend.localCache.set(key, res)
      }
      return res
    }
  }

  static loadId(): DataObjectId {
    if (Backend.userId) {
      return Backend.userId
    } else {
      const userId = Backend.loadLocal('user_id') as DataObjectId
      Backend.userId = userId
      return userId
    }
  }

  static loadKey(): string {
    if (!Backend.key) {
      const key = Backend.loadLocal('user_key')
      Backend.key = key
    }
    return Backend.key
  }

  static loadCred(): {userId: DataObjectId; key: string} {
    return {
      userId: Backend.loadId(),
      key: Backend.loadKey()
    }
  }

  static async logout(key: string): Promise<void> {
    if (!this.requireLogout) {
      return
    }
    await this.postJSON('/data/logout', {key})
  }

  static clearCredentials(): void {
    Backend.userId = undefined
    Backend.key = undefined
    Backend.removeLocal('user_id')
    Backend.removeLocal('user_key')
    Backend.removeLocal('theme')
    Backend.removeLocal('dialog-no-show-more')
    Backend.saveLocal('logout', Time.now() + '')
    Backend.resetCredCookies()
  }

  static saveCredentials(id: DataObjectId, key: string): void {
    Backend.userId = id
    Backend.key = key
    Backend.saveLocal('user_id', id)
    Backend.saveLocal('user_key', key)
    Backend.saveLocal('login', Time.now() + '')
    Backend.setCredCookies()
  }

  static removeLocal(key: string): void {
    Storage.removeItem(key)
    Backend.localCache.delete(key)
    Backend.notifyLocalKeyChanged(key)
  }

  static addLocalListener(key: string, onChange: () => void): UUID {
    const uuid = Util.UUID()
    if (!Backend.localListeners.has(key)) {
      Backend.localListeners.set(key, new Map())
    }
    Backend.localListeners.get(key)?.set(uuid, onChange)
    return uuid
  }

  static removeLocalListener(key: string, uuid: UUID): void {
    if (
      !Backend.localListeners.has(key) ||
      !Backend.localListeners.get(key)?.has(uuid)
    ) {
      logger.error('Listener not found')
      return
    }
    Backend.localListeners.get(key)?.delete(uuid)
  }

  private static notifyLocalKeyChanged(key: string): void {
    if (Backend.localListeners.has(key)) {
      Backend.localListeners.get(key)?.forEach((onChange: () => void) => {
        onChange()
      })
    }
  }

  private static get hostnames(): string[] {
    return [Backend.host, Backend.dataUrl, window?.location?.hostname].filter(
      Boolean
    )
  }

  private static setCredCookies() {
    const key = Backend.loadKey()
    const userId = Backend.loadId()
    const keyTimestamp = parseInt(key.split('_')[0], 10)
    const cookieExpiration =
      keyTimestamp + this.keyExpirationTimeout + 24 * 60_000
    Backend.hostnames.forEach(host => {
      Cookies.setItem('user_key', key, new Date(cookieExpiration), host)
      Cookies.setItem('user_id', userId, new Date(cookieExpiration), host)
      if (Util.isDesktop()) {
        DesktopRenderer.setCredCookies(
          'user_id',
          userId,
          cookieExpiration,
          host
        )
        DesktopRenderer.setCredCookies('user_key', key, cookieExpiration, host)
      }
    })
  }

  private static resetCredCookies() {
    Backend.hostnames.forEach(host => {
      Cookies.removeItem('user_key', host)
      Cookies.removeItem('user_id', host)
      if (Util.isDesktop()) {
        DesktopRenderer.setCredCookies('user_id', undefined, 0, host)
        DesktopRenderer.setCredCookies('user_key', undefined, 0, host)
      }
    })
  }
}

export class NetworkError extends Error {
  status: number
}
